import { useEffect, useMemo, useRef, useState } from 'react';
import { AlertCircle, Pencil, TrendingUp, TrendingDown } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { AreaChart, Area, CartesianGrid, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TEAL = '#2EB4AC';

// Compact integer formatter — "1.3k", "12.4k", "910"
const compact = (n) => {
  const v = Number(n) || 0;
  if (v >= 1000) return `${(v / 1000).toFixed(v >= 10000 ? 0 : 1)}k`;
  return `${Math.round(v)}`;
};

const inrFmt = (n) => `\u20B9${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

// Stale = last update is older than the most recent Monday 00:00 local
const lastMondayMidnight = () => {
  const now = new Date();
  const daysSinceMon = (now.getDay() + 6) % 7; // Mon=0 ... Sun=6
  const d = new Date(now);
  d.setDate(now.getDate() - daysSinceMon);
  d.setHours(0, 0, 0, 0);
  return d;
};

export default function CostTrackerWidget() {
  const [state, setState] = useState(null);
  const [history, setHistory] = useState([]);
  const [expanded, setExpanded] = useState(false);
  const [open, setOpen] = useState(false);
  const [draftCredits, setDraftCredits] = useState('');
  const [draftRate, setDraftRate] = useState('');
  const [saving, setSaving] = useState(false);
  const pillRef = useRef(null);

  const fetchState = async () => {
    try {
      const r = await fetch(`${API}/cost-tracker`, { credentials: 'include' });
      if (r.ok) setState(await r.json());
    } catch (e) { /* silent */ }
  };
  const fetchHistory = async () => {
    try {
      const r = await fetch(`${API}/cost-tracker/history?weeks=12`, { credentials: 'include' });
      if (r.ok) {
        const data = await r.json();
        setHistory(data.history || []);
      }
    } catch (e) { /* silent */ }
  };

  useEffect(() => { fetchState(); fetchHistory(); }, []);

  // Click-outside collapses the expanded pill
  useEffect(() => {
    if (!expanded) return;
    const onDown = (e) => {
      if (pillRef.current && !pillRef.current.contains(e.target)) setExpanded(false);
    };
    window.addEventListener('mousedown', onDown);
    return () => window.removeEventListener('mousedown', onDown);
  }, [expanded]);

  const stale = useMemo(() => (
    !state?.updated_at || new Date(state.updated_at) < lastMondayMidnight()
  ), [state]);

  const credits = Number(state?.total_credits || 0);
  const rate = Number(state?.cost_per_credit_inr || 13.70);
  const totalSpentInr = credits * rate;

  // Trend delta between last two history points
  const delta = useMemo(() => {
    if (history.length < 2) return null;
    const last = history[history.length - 1].credits;
    const prev = history[history.length - 2].credits;
    const diff = last - prev;
    const pct = prev > 0 ? (diff / prev) * 100 : (last > 0 ? 100 : 0);
    return { diff, pct, up: diff >= 0 };
  }, [history]);

  // Tiny inline sparkline for the expanded pill
  const Sparkline = () => {
    if (history.length < 2) return null;
    const W = 56, H = 18, P = 1;
    const vals = history.map(h => h.credits);
    const max = Math.max(...vals, 1);
    const min = Math.min(...vals, 0);
    const range = max - min || 1;
    const pts = vals.map((v, i) => {
      const x = P + (i * (W - 2 * P)) / (vals.length - 1);
      const y = H - P - ((v - min) / range) * (H - 2 * P);
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    });
    const last = pts[pts.length - 1].split(',');
    return (
      <svg width={W} height={H} className="block">
        <polyline points={pts.join(' ')} fill="none" stroke={TEAL} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <circle cx={last[0]} cy={last[1]} r="2" fill={TEAL} />
      </svg>
    );
  };

  const openDialog = () => {
    setDraftCredits(state?.total_credits ? String(state.total_credits) : '');
    setDraftRate(state?.cost_per_credit_inr ? String(state.cost_per_credit_inr) : '13.70');
    setOpen(true);
  };

  const handleSave = async () => {
    const c = parseFloat(draftCredits);
    const r = parseFloat(draftRate);
    if (!isFinite(c) || c < 0) { toast.error('Enter valid credits'); return; }
    if (!isFinite(r) || r < 0) { toast.error('Enter valid rate'); return; }
    setSaving(true);
    try {
      const res = await fetch(`${API}/cost-tracker`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ total_credits: c, cost_per_credit_inr: r }),
      });
      if (!res.ok) throw new Error('save failed');
      await fetchState();
      await fetchHistory();
      toast.success('Cost tracker updated');
      setOpen(false);
      setExpanded(false);
    } catch (e) {
      toast.error('Could not save — try again');
    } finally {
      setSaving(false);
    }
  };

  const livePreview = useMemo(() => {
    const c = parseFloat(draftCredits) || 0;
    const r = parseFloat(draftRate) || 0;
    return c * r;
  }, [draftCredits, draftRate]);

  const lastUpdatedLabel = state?.updated_at
    ? `Last updated ${format(parseISO(state.updated_at), 'PPp')} by ${(state.updated_by || '').split('@')[0]}`
    : 'Not updated yet — be the first to log credits this week';

  // Stale red dot (used in collapsed icon AND on expanded pill when no history)
  const StaleDot = () => (
    <span className="absolute -top-0.5 -right-0.5 flex h-2.5 w-2.5" data-testid="cost-tracker-stale-dot">
      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
      <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-red-500 border-2 border-white" />
    </span>
  );

  // Chart prep — convert week_start strings to Date for X-axis
  const chartData = useMemo(() => history.map(h => ({
    ...h,
    date: parseISO(h.week_start),
  })), [history]);

  return (
    <>
      <div ref={pillRef} className="relative" data-testid="cost-tracker-widget">
        {!expanded ? (
          <button
            type="button"
            aria-label="App development cost tracker"
            data-testid="cost-tracker-collapsed"
            onClick={() => setExpanded(true)}
            className="relative h-10 w-10 rounded-full hover:bg-gray-100 transition-colors flex items-center justify-center"
          >
            <img src="/icons/cost-rupee.png" alt="" className="h-7 w-7 hover:scale-110 transition-transform" />
            {stale && <StaleDot />}
          </button>
        ) : (
          <button
            type="button"
            data-testid="cost-tracker-expanded"
            onClick={openDialog}
            className="group relative h-11 rounded-xl border border-slate-200 bg-white hover:border-slate-300 hover:shadow-sm flex items-stretch overflow-hidden animate-in fade-in slide-in-from-right-2 duration-200"
          >
            {/* Left cell — credits */}
            <div className="flex items-center gap-2 px-3 bg-slate-50/50 border-r border-slate-200">
              <img src="/icons/cost-rupee.png" alt="" className="h-4 w-4 opacity-80" />
              <div className="flex flex-col items-start leading-none">
                <span className="text-[9px] font-semibold uppercase text-slate-400 tracking-wider">Credits</span>
                <span className="text-sm font-bold tabular-nums text-slate-800 mt-0.5">{compact(credits)}</span>
              </div>
            </div>
            {/* Right cell — spent + sparkline */}
            <div className="flex items-center gap-2.5 px-3">
              <div className="flex flex-col items-start leading-none">
                <span className="text-[9px] font-semibold uppercase tracking-wider" style={{ color: TEAL }}>Spent</span>
                <span className="text-sm font-bold tabular-nums text-slate-800 mt-0.5">{inrFmt(totalSpentInr)}</span>
              </div>
              <Sparkline />
              {stale ? <StaleDot /> : (
                <Pencil className="h-3.5 w-3.5 text-slate-300 opacity-0 group-hover:opacity-100 transition-opacity" />
              )}
            </div>
          </button>
        )}
      </div>

      {/* Edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="cost-tracker-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <img src="/icons/cost-rupee.png" alt="" className="h-5 w-5" />
              App Development Cost Tracker
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {stale && (
              <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2.5" data-testid="cost-tracker-stale-alert">
                <AlertCircle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
                <p className="text-xs text-amber-800 leading-snug">It's a new week — please update the credits consumed since last Monday.</p>
              </div>
            )}

            {/* Trendline (≥2 history entries) */}
            {history.length >= 2 ? (
              <div className="border border-slate-200 rounded-xl p-3 bg-white">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">Week-on-week credits</div>
                    <div className="text-[11px] text-slate-400 mt-0.5">Last {history.length} weeks · Mondays</div>
                  </div>
                  {delta && (
                    <div
                      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold ${delta.up ? 'bg-rose-50 text-rose-600' : 'bg-emerald-50 text-emerald-600'}`}
                      title="Change vs prior week"
                    >
                      {delta.up ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                      <span>{delta.up ? '+' : ''}{Math.round(delta.diff)} cr ({delta.up ? '+' : ''}{delta.pct.toFixed(0)}%)</span>
                    </div>
                  )}
                </div>
                <div style={{ height: 176 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={chartData} margin={{ top: 8, right: 8, left: -10, bottom: 0 }}>
                      <defs>
                        <linearGradient id="ctGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor={TEAL} stopOpacity={0.35} />
                          <stop offset="100%" stopColor={TEAL} stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                      <XAxis
                        dataKey="date"
                        tickFormatter={(d) => format(d, 'd MMM')}
                        tick={{ fontSize: 10, fill: '#64748b' }}
                        axisLine={false}
                        tickLine={false}
                      />
                      <YAxis
                        tick={{ fontSize: 10, fill: '#64748b' }}
                        tickFormatter={(v) => compact(v)}
                        axisLine={false}
                        tickLine={false}
                        width={40}
                      />
                      <Tooltip
                        contentStyle={{ borderRadius: 12, border: '1px solid #e2e8f0', fontSize: 12, padding: '6px 10px' }}
                        labelFormatter={(d) => format(d, 'd MMM yyyy')}
                        formatter={(v, n) => n === 'credits' ? [`${Number(v).toLocaleString('en-IN')} cr`, 'Credits'] : [`${inrFmt(v)}`, 'Spent']}
                      />
                      <Area type="monotone" dataKey="credits" stroke={TEAL} strokeWidth={2} fill="url(#ctGrad)" dot={{ r: 3, fill: '#fff', stroke: TEAL, strokeWidth: 2 }} activeDot={{ r: 4 }} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>
            ) : (
              <div className="border border-dashed border-slate-200 rounded-xl px-4 py-5 text-center bg-slate-50/40" data-testid="cost-tracker-empty">
                <p className="text-xs text-slate-500">Save updates each week to build the trendline.</p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-slate-700 mb-1 block">Total credits consumed</label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={draftCredits}
                  onChange={(e) => setDraftCredits(e.target.value)}
                  placeholder="e.g. 1450"
                  data-testid="cost-tracker-credits-input"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-700 mb-1 block">Per-credit cost (₹)</label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={draftRate}
                  onChange={(e) => setDraftRate(e.target.value)}
                  placeholder="13.70"
                  data-testid="cost-tracker-rate-input"
                />
                <p className="text-[10px] text-slate-400 mt-1">Default ₹13.70 — edit anytime as billing changes.</p>
              </div>
            </div>

            <div className="bg-slate-50/60 border border-slate-200 rounded-lg px-4 py-3 flex items-center justify-between">
              <span className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Live Total</span>
              <span className="text-xl font-bold tabular-nums text-slate-800" data-testid="cost-tracker-live-total">{inrFmt(livePreview)}</span>
            </div>

            <p className="text-[11px] text-slate-400">{lastUpdatedLabel}</p>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} disabled={saving}>Cancel</Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-[#2EB4AC] hover:bg-[#259993] text-white"
              data-testid="cost-tracker-save"
            >
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
